import { Box } from "@mui/material";
import { Theme, keyframes, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useColorMode } from "src/design/ColorModeProvider";
import { radius } from "src/design/tokens";

export type StatusChipStatus =
  | "planned"
  | "open"
  | "review"
  | "finalized"
  | "synced"
  | "pending"
  | "offline"
  | "manual"
  | "connected"
  | "attention"
  | "unchecked"
  | "active"
  | "invited"
  | "deactivated"
  | "expired"
  | "revoked"
  | "accepted";

export interface StatusChipProps {
  status: StatusChipStatus;
  /** Number of unsynced entries; only used by `pending`. */
  count?: number;
}

type Tone = "live" | "neutral" | "warning" | "primary" | "success";

const TONE: Record<StatusChipStatus, Tone> = {
  open: "live",
  planned: "neutral",
  review: "warning",
  finalized: "primary",
  synced: "success",
  pending: "warning",
  offline: "warning",
  manual: "neutral",
  connected: "success",
  attention: "warning",
  unchecked: "neutral",
  active: "success",
  invited: "primary",
  deactivated: "neutral",
  expired: "warning",
  revoked: "neutral",
  accepted: "success",
};

const colorsFor = (theme: Theme, tone: Tone) => {
  const p = theme.palette;
  switch (tone) {
    case "live":
      return { bg: p.error.main, fg: p.onTier, border: "transparent" };
    case "warning":
      return { bg: p.warning.light, fg: p.warning.main, border: "transparent" };
    case "primary":
      return { bg: p.primary.light, fg: p.primaryInk, border: "transparent" };
    case "success":
      return { bg: p.success.light, fg: p.successInk, border: "transparent" };
    default:
      return { bg: p.surfaceAlt, fg: p.text.primary, border: "transparent" };
  }
};

const pulse = keyframes`
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.7); }
  100% { opacity: 1; transform: scale(1); }
`;

/** Pill showing a session, sync, source, person or invitation status. The text label is always present. */
export const StatusChip = ({ status, count }: StatusChipProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { reducedMotion } = useColorMode();
  const tone = TONE[status];
  const { bg, fg, border } = colorsFor(theme, tone);

  const label = (() => {
    switch (status) {
      case "pending":
        return count === undefined
          ? t("console.status.pending", { defaultValue: "Pending" })
          : t("station.sync.pending", { count });
      case "synced":
      case "offline":
        return t(`station.sync.${status}`);
      case "manual":
        return t("classes.source.manual");
      case "connected":
        return t("rostering.connected");
      case "attention":
        return t("rostering.chip.attention");
      case "unchecked":
        return t("rostering.chip.unchecked");
      case "active":
      case "invited":
      case "deactivated":
      case "expired":
      case "revoked":
      case "accepted":
        return t(`console.status.${status}`);
      default:
        return t(`sessions.status.${status}`);
    }
  })();

  return (
    <Box
      component="span"
      data-status={status}
      data-tone={tone}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        px: "11px",
        py: "7px",
        minHeight: 24,
        borderRadius: `${radius.pill}px`,
        bgcolor: bg,
        color: fg,
        boxShadow:
          border === "transparent" ? "none" : `inset 0 0 0 1px ${border}`,
        // Design `STATUS_CHIP`: 800 10.5–11 px expanded Archivo, no tracking.
        fontSize: 10.5,
        fontWeight: 800,
        lineHeight: 1,
        textTransform: "uppercase",
        fontStretch: "125%",
        whiteSpace: "nowrap",
      }}
    >
      {status === "open" && (
        <Box
          component="span"
          aria-hidden="true"
          data-testid="live-dot"
          data-animated={reducedMotion ? "false" : "true"}
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: "currentColor",
            animation: reducedMotion
              ? "none"
              : `${pulse} 1.4s ease-in-out infinite`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      )}
      {label}
    </Box>
  );
};

export default StatusChip;
